import { useContext, useState } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { MdForward10, MdReplay10 } from "react-icons/md";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";
import { IoMdArrowDropdown, IoMdArrowDropup, IoMdClose, IoMdShare } from "react-icons/io";
import { ActionIcon, Affix, Anchor, Button, Group, Paper, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import styles from "./styles/Player.module.css";
import { MarqueeText } from "./MarqueeText";
import { MiniProgress, PlayerClock, PlayerTimeline } from "./PlayerProgress";
import { SleepTimerButton } from "./SleepTimerButton";
import { AudioContext, AudioState } from "@/context/audio";
import { trackEvent } from "@/core/analytics";
import { useStores } from "@/hooks/useStores";
import { SignInPrompt } from "@/components/auth/SignInPrompt";
import { usePlayEpisode } from "@/hooks/usePlayEpisode";
import { shareMoment } from "@/core/shareMoment";
import { Urls } from "@/core/urls";
import { filterTitle, getPodcastImage, ImageSizes, timeAgo } from "@/core/helpers";

const PlayerToggle = observer(({ episode, podcast }: { episode: Episode; podcast: Podcast }) => {
    const { play, pause, isPlaying } = usePlayEpisode(episode, podcast);

    return (
        <button
            aria-label={isPlaying ? "Pausa avsnitt" : "Spela avsnitt"}
            className={styles.toggle}
            onClick={isPlaying ? pause : play}
            type="button">
            {isPlaying ? <BsPauseFill size={26} /> : <BsFillPlayFill size={26} />}
        </button>
    );
});

export const Player = observer(() => {
    const { playerStore } = useStores();
    const sound = useContext<AudioState>(AudioContext);

    const ratios = [0.5, 0.75, 1, 1.25, 1.5, 2];
    // On a phone the player is one slim row; the rest opens on demand
    const isMobile = useMediaQuery("(max-width: 48em)") ?? false;
    const [expanded, setExpanded] = useState(false);
    const compact = isMobile && !expanded;
    const currentRate = sound.rate();
    const ratio = Math.max(0, ratios.indexOf(currentRate));

    const seekTo = (value: number) => {
        sound.seek(value);
        trackEvent({
            category: "Player",
            action: "SliderClick",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
            value,
        });
    };

    const soundLoaded = () => sound.audio !== undefined;
    const getCurrentTime = () => (soundLoaded() ? sound.seek() : null);

    const close = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        playerStore.close();
        sound.unload();
    };

    const jump = (jumpAmount: number) => {
        const current = getCurrentTime();
        if (current !== null) {
            const next = Math.max(0, Math.floor(current) + jumpAmount);
            sound.seek(next);
        }
    };

    const share = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        const current = getCurrentTime();
        if (current === null) return;
        void shareMoment(playerStore.currentPodcast, playerStore.currentEpisode, current);
        trackEvent({
            category: "Player",
            action: "ShareMoment",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
            value: Math.floor(current),
        });
    };

    const rewind = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        jump(-10);
        trackEvent({
            category: "Player",
            action: "Rewind",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    const forward = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        jump(10);
        trackEvent({
            category: "Player",
            action: "Forward",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    const showRatio = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        const maxLength = ratios.length - 1;
        const next = ratio === maxLength ? 0 : ratio + 1;
        sound.rate(ratios[next]);
        trackEvent({
            category: "Player",
            action: "Ratio",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    if (!playerStore.active) {
        return null;
    }

    if (compact) {
        return (
            <Affix className={styles.affix} position={{ bottom: 16, left: 0, right: 0 }}>
                <Paper className={`${styles.player} ${styles.mini}`} p="xs" radius="md" withBorder>
                    <Group gap="xs" wrap="nowrap">
                        <Anchor
                            component={Link}
                            to={Urls.podcastHome(playerStore.currentPodcast.slug)}
                            underline="never">
                            <img
                                alt="podcast img"
                                className={styles.miniimg}
                                decoding="async"
                                height={40}
                                src={getPodcastImage(playerStore.currentPodcast, ImageSizes.Thumb)}
                                width={40}
                            />
                        </Anchor>
                        <Stack className={styles.meta} gap={0}>
                            <Anchor
                                component={Link}
                                className={styles.minititle}
                                to={`/podd/${playerStore.currentPodcast.slug}/${playerStore.currentEpisode.slug}`}
                                underline="never">
                                <MarqueeText
                                    text={filterTitle(
                                        playerStore.currentEpisode.title,
                                        playerStore.currentPodcast.name
                                    )}
                                />
                            </Anchor>
                            <Text c="dimmed" lineClamp={1} size="xs">
                                {playerStore.currentPodcast.name}
                            </Text>
                        </Stack>
                        <PlayerToggle
                            episode={playerStore.currentEpisode}
                            key={playerStore.currentEpisode.guid}
                            podcast={playerStore.currentPodcast}
                        />
                        <ActionIcon
                            aria-expanded={false}
                            aria-label="Visa fler inställningar för spelaren"
                            onClick={() => setExpanded(true)}
                            radius="md"
                            size="lg"
                            variant="subtle">
                            <IoMdArrowDropup size={22} />
                        </ActionIcon>
                        <ActionIcon aria-label="Stäng spelaren" onClick={close} radius="md" size="lg" variant="subtle">
                            <IoMdClose size={18} />
                        </ActionIcon>
                    </Group>
                    <MiniProgress />
                </Paper>
            </Affix>
        );
    }

    return (
        <Affix className={styles.affix} position={{ bottom: 16, left: 0, right: 0 }}>
            <Paper className={styles.player} px="md" py="md" radius="md" withBorder>
                <Group className={styles.close} gap={4} wrap="nowrap">
                    {isMobile && (
                        <ActionIcon
                            aria-expanded
                            aria-label="Visa färre inställningar för spelaren"
                            onClick={() => setExpanded(false)}
                            radius="md"
                            size="lg"
                            variant="subtle">
                            <IoMdArrowDropdown size={22} />
                        </ActionIcon>
                    )}
                    <ActionIcon aria-label="Stäng spelaren" onClick={close} radius="md" size="lg" variant="subtle">
                        <IoMdClose size={18} />
                    </ActionIcon>
                </Group>

                <Group align="flex-start" className={styles.header} gap="md" wrap="nowrap">
                    <Anchor component={Link} to={Urls.podcastHome(playerStore.currentPodcast.slug)} underline="never">
                        <img
                            alt="podcast img"
                            className={styles.podcastimg}
                            decoding="async"
                            height={64}
                            loading="lazy"
                            src={getPodcastImage(playerStore.currentPodcast, ImageSizes.Small)}
                            width={64}
                        />
                    </Anchor>

                    <Stack className={styles.meta} gap={4}>
                        <Anchor
                            component={Link}
                            className={styles.title}
                            to={`/podd/${playerStore.currentPodcast.slug}/${playerStore.currentEpisode.slug}`}
                            underline="never">
                            <span>
                                {filterTitle(playerStore.currentEpisode.title, playerStore.currentPodcast.name)}
                            </span>
                        </Anchor>
                        <Group gap="xs" wrap="wrap">
                            <Anchor
                                component={Link}
                                fw={500}
                                size="sm"
                                to={`/podd/${playerStore.currentPodcast.slug}`}
                                underline="hover">
                                {playerStore.currentPodcast.name}
                            </Anchor>
                            <Text c="dimmed" size="sm">
                                {timeAgo(playerStore.currentEpisode.published)}
                            </Text>
                        </Group>
                        <SignInPrompt compact />
                    </Stack>
                </Group>

                <div className={styles.timeline}>
                    <PlayerTimeline onSeek={seekTo} />
                </div>

                <Group className={styles.controls} justify="space-between" wrap="wrap">
                    <Group className={styles.transport} gap="xs" wrap="nowrap">
                        <ActionIcon
                            aria-label="Spola tillbaka tio sekunder"
                            onClick={rewind}
                            radius="xl"
                            size="xl"
                            variant="subtle">
                            <MdReplay10 size={28} />
                        </ActionIcon>
                        <PlayerToggle
                            episode={playerStore.currentEpisode}
                            key={playerStore.currentEpisode.guid}
                            podcast={playerStore.currentPodcast}
                        />
                        <ActionIcon
                            aria-label="Spola fram tio sekunder"
                            onClick={forward}
                            radius="xl"
                            size="xl"
                            variant="subtle">
                            <MdForward10 size={28} />
                        </ActionIcon>
                    </Group>

                    <PlayerClock />

                    <Group className={styles.settings} gap="xs" wrap="nowrap">
                        <ActionIcon
                            aria-label="Dela den här stunden"
                            onClick={share}
                            radius="xl"
                            size="lg"
                            variant="subtle">
                            <IoMdShare size={18} />
                        </ActionIcon>

                        <Button
                            aria-label="Ändra uppspelningshastighet"
                            onClick={showRatio}
                            radius="xl"
                            size="compact-md"
                            variant="light">
                            {Math.round(ratios[ratio] * 100)}%
                        </Button>

                        <SleepTimerButton />
                    </Group>
                </Group>
            </Paper>
        </Affix>
    );
});
